import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import useTheme from '@/hooks/useNeutralTheme';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';


function CreditPackCard({
  credits,
  price,
  name,
  isBestValue,
  isPopular,
  isSelected,
  onSelect,
  savingsPercent,
  priceLabel,
  disabled,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isDark, isXS), [colors, isDark, isXS]);

  const hasTag = isBestValue || isPopular;
  const tagLabel = isBestValue ? 'Best value' : isPopular ? 'Popular' : null;
  const tagStyle = isBestValue ? styles.bestValueTag : isPopular ? styles.popularTag : null;

  return (
    <Pressable
      accessibilityRole="radio"
      aria-checked={!!isSelected}
      accessibilityState={{ checked: !!isSelected, disabled: !!disabled }}
      accessibilityLabel={`${(credits ?? 0).toLocaleString()} credits, ${priceLabel}`}
      onPress={onSelect}
      disabled={disabled}
      style={({ hovered }) => [
        styles.card,
        isSelected && styles.cardSelected,
        hovered && !isSelected && !disabled && styles.cardHovered,
        disabled && styles.cardDisabled,
      ]}
    >
      {hasTag && (
        <View style={[styles.tag, tagStyle]}>
          <Text size="xs" weight="bold" style={styles.tagText}>
            {tagLabel}
          </Text>
        </View>
      )}

      <View style={styles.row}>
        <View style={[styles.radio, isSelected && styles.radioSelected]}>
          {isSelected && <View style={styles.radioDot} />}
        </View>

        <View style={styles.creditInfo}>
          <View style={styles.creditRow}>
            <Icon name="coins" size={14} color={colors.text_color_primary} solid />
            <Text size="l" weight="bold" color="primary">
              {(credits ?? 0).toLocaleString()}
            </Text>
            <Text size="xs" color="secondary" style={styles.creditsLabel}>credits</Text>
          </View>
          {savingsPercent > 0 && (
            <View style={styles.metaRow}>
              <View style={styles.savingsBadge}>
                <Text size="xs" weight="bold" style={styles.savingsText}>
                  {savingsPercent}% off
                </Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.priceContainer}>
          <Text size="l" weight="bold" color="primary">{priceLabel}</Text>
          <Text size="xs" color="tertiary">one-time</Text>
        </View>
      </View>
    </Pressable>
  );
}

const createStyles = (colors, isDark, isXS) => StyleSheet.create({
  card: {
    position: 'relative',
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: colors.border_color_primary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
    overflow: 'hidden',
    width: isXS ? '100%' : 'calc(33.333% - 8px)',
    minWidth: isXS ? undefined : 200,
    cursor: 'pointer',
    ...Platform.select({
      web: {
        transitionProperty: 'border-color, background-color, box-shadow',
        transitionDuration: '150ms',
      },
    }),
  },
  cardSelected: {
    borderColor: colors.text_color_primary,
    backgroundColor: colors.solid_color_tertiary,
    ...Platform.select({
      web: {
        boxShadow: `0 0 0 1px ${colors.text_color_primary}40`,
      },
    }),
  },
  cardHovered: {
    borderColor: colors.text_color_primary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)',
  },
  cardDisabled: {
    opacity: 0.5,
  },
  tag: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderBottomLeftRadius: 8,
    zIndex: 1,
  },
  bestValueTag: {
    backgroundColor: colors.text_color_primary,
  },
  popularTag: {
    backgroundColor: colors.text_color_primary,
  },
  tagText: {
    color: colors.accent_text_color,
    fontSize: 11,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: isXS ? 14 : 16,
    gap: 14,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.border_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioSelected: {
    borderColor: colors.text_color_primary,
    borderWidth: 2,
  },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.text_color_primary,
  },
  creditInfo: {
    flex: 1,
    gap: 3,
  },
  creditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  creditsLabel: {
    marginTop: 1,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  savingsBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
    backgroundColor: colors.solid_color_tertiary,
  },
  savingsText: {
    color: colors.text_color_primary,
    fontSize: 11,
  },
  priceContainer: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
});

export default React.memo(CreditPackCard);
